import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, nullable, number, object } from "valibot";
import { dataName, gridFSFile, label, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { isNil } from "lodash-es";
import { EXISTS_CONDITION_KEYS } from "../../constants";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import Image from "../valueRendering/Image";
import type { SelectOption } from "../../types/Menu";
import { SingleSelectInput } from "../../components/menu/Select";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";
import FieldValueReference from "../FieldValueReference.ts";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil.ts";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  wide: fallback(boolean(), false),
  stroke_width: nullable(number()),
  required: required(),
});
const ValueSchema = gridFSFile();

export type SignatureFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const SignatureField: Field<SignatureFieldProperties, DefaultDtoType, ValueType> = {
  type: "signature",
  category: "personalInfo",
  widget: "com.moreapps:signature:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    wide: false,
    stroke_width: 3,
    required: false,
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => value,
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();

    return (
      <Image
        fileId={value}
        scope="submission"
        ariaLabel={t(SignatureField.titleKey)}
        modalTitle={fieldConfig.properties.label_text}
        modalIcon={SignatureField.icon}
        modalCategory={SignatureField.category}
      />
    );
  },
  icon: "pencilAlt",
  titleKey: keyFromSelector(($) => $.SIGNATURE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.SIGNATURE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    wide: keyFromSelector(($) => $.FIELD_PROPERTY_WIDE),
    stroke_width: keyFromSelector(($) => $.FIELD_PROPERTY_STROKE_WIDTH),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
  },
  valueReferenceOptions: (id, props) => {
    const referenceId = FieldValueReference.from(id.toString(), "image");
    const icon = getDynamicValueIcon(referenceId, "signature");
    return [
      {
        variant: "dynamicValue",
        value: referenceId,
        label: props.label_text,
        pill: props.data_name,
        icon,
      },
    ];
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <FieldButtonPreview icon="pencil" text={t(($) => $.SIGNATURE_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    const strokeWidths: SelectOption<number>[] = [
      { variant: "text", value: 3, label: t(($) => $.FIELD_PROPERTY_STROKE_WIDTH_SMALL) },
      { variant: "text", value: 6, label: t(($) => $.FIELD_PROPERTY_STROKE_WIDTH_MEDIUM) },
      { variant: "text", value: 9, label: t(($) => $.FIELD_PROPERTY_STROKE_WIDTH_LARGE) },
    ];

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_CANVAS)}>
          <SingleSelectInput
            value={toStrokeWidth(props.data.properties.stroke_width)}
            options={strokeWidths}
            onSelect={(value) => props.setValue("stroke_width", value)}
            label={t(props.propertyKeys.stroke_width)}
            sameWidth
          />
          <Toggle
            block
            label={t(props.propertyKeys.wide)}
            checked={props.data.properties.wide}
            setChecked={(value) => props.setValue("wide", value)}
            description={t(($) => $.FIELD_PROPERTY_WIDE_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

const toStrokeWidth = (value: number | null) => {
  if (isNil(value)) {
    return null;
  }
  if (value <= 3) {
    return 3;
  }
  if (value < 8) {
    return 6;
  }
  return 9;
};

export default initField(SignatureField);
